/**
 * Auth Service for Asertis API
 * Handles JWT token management with auto-refresh on expiry.
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_LOGIN_USER = process.env.REACT_APP_API_LOGIN_USER;
const API_LOGIN_PASS = process.env.REACT_APP_API_LOGIN_PASS;
const APP_CORS = process.env.REACT_APP_CORS || "";

let accessToken = null;
let tokenExpiry = null;

const buildApiUrl = (url) => {
    if (!url || !APP_CORS) {
        return url;
    }

    const normalizedUrl = String(url);

    if (!/^https?:\/\//i.test(normalizedUrl)) {
        return normalizedUrl;
    }

    if (normalizedUrl.startsWith(APP_CORS)) {
        return normalizedUrl;
    }

    return `${APP_CORS}${normalizedUrl}`;
};

/**
 * Login to Asertis API and store token in memory.
 */
const login = async () => {
    try {
        const response = await fetch(buildApiUrl(`${API_BASE_URL}/login`), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify({
                login: API_LOGIN_USER,
                password: API_LOGIN_PASS,
            }),
        });

        if (!response.ok) {
            throw new Error(`Login failed: ${response.status}`);
        }

        const data = await response.json();
        accessToken = data.access_token;
        // Refresh 60 seconds before actual expiry to avoid edge cases
        tokenExpiry = Date.now() + (data.expires_in - 60) * 1000;
        console.log("Asertis API: Token obtenido correctamente");
        return accessToken;
    } catch (error) {
        console.error("Error al iniciar sesión en Asertis API:", error);
        throw error;
    }
};

/**
 * Get current token, refreshing if expired or about to expire.
 */
const getValidToken = async () => {
    if (!accessToken || Date.now() >= tokenExpiry) {
        await login();
    }
    return accessToken;
};

/**
 * Wrapper around fetch that injects Bearer token and auto-refreshes on 401.
 */
const authFetch = async (url, options = {}) => {
    let token = await getValidToken();

    const makeRequest = async (authToken) => {
        const headers = {
            Accept: "application/json",
            ...options.headers,
            Authorization: `Bearer ${authToken}`,
        };

        if (options.body && !headers["Content-Type"]) {
            headers["Content-Type"] = "application/json";
        }

        return fetch(buildApiUrl(url), { ...options, headers });
    };

    let response = await makeRequest(token);

    // If 401 Unauthorized, refresh token and retry once
    if (response.status === 401) {
        console.log("Asertis API: Token expirado, renovando...");
        accessToken = null;
        tokenExpiry = null;
        token = await getValidToken();
        response = await makeRequest(token);
    }

    return response;
};

/**
 * Clear stored token (for logout or reset).
 */
const clearToken = () => {
    accessToken = null;
    tokenExpiry = null;
};

export { login, getValidToken, authFetch, clearToken, buildApiUrl };
